<template>
	<div class="api-tokens">
		<div class="api-tokens__header">
			<h2 class="api-tokens__title">API Tokens</h2>
			<p class="api-tokens__description">
				Create and manage personal access tokens for API authentication from external tools and
				automation scripts.
			</p>
		</div>

		<div class="api-tokens__create-section">
			<h3 class="api-tokens__section-title">Create New Token</h3>
			<form
				class="api-tokens__form"
				@submit.prevent="handleCreateToken">
				<FormInput label="Token Name">
					<InputText
						v-model="newTokenName"
						placeholder="e.g., MCP Server, CI/CD Pipeline"
						:disabled="creating" />
				</FormInput>
				<FormInput label="Expiration (days)">
					<InputNumber
						v-model="newTokenExpiry"
						placeholder="Defaults to 1 year"
						:disabled="creating"
						:max="365"
						:min="1" />
				</FormInput>
				<Button
					class="api-tokens__form-submit"
					icon="fal fa-plus"
					label="Create Token"
					type="submit"
					:disabled="!newTokenName.trim()"
					:loading="creating" />
			</form>
		</div>

		<Dialog
			v-model:visible="showNewTokenDialog"
			class="api-tokens__dialog"
			header="Token Created Successfully"
			:closable="true"
			:modal="true">
			<div class="api-tokens__new-token-content">
				<Message
					severity="warn"
					:closable="false">
					Make sure to copy your token now. You won't be able to see it again!
				</Message>
				<div class="api-tokens__token-display">
					<code class="api-tokens__token-value">{{ newlyCreatedToken }}</code>
					<Button
						aria-label="Copy token"
						icon="fa regular fa-copy"
						severity="secondary"
						text
						@click="copyToken" />
				</div>
				<Message
					v-if="copied"
					severity="success"
					:closable="false">
					Token copied to clipboard!
				</Message>
			</div>
		</Dialog>

		<div class="api-tokens__list-section">
			<h3 class="api-tokens__section-title">Your Tokens</h3>
			<DataTable
				class="api-tokens__table"
				responsive-layout="scroll"
				:value="tokenState.tokens.value">
				<template #empty>
					<div class="api-tokens__empty">No API tokens found. Create one above.</div>
				</template>
				<Column
					field="name"
					header="Name" />
				<Column header="Created">
					<template #body="{ data }">
						{{ formatDate(data.created_at) }}
					</template>
				</Column>
				<Column header="Expires">
					<template #body="{ data }">
						<span :class="{ 'api-tokens__expired': isExpired(data.expires_at) }">
							{{ data.expires_at ? formatDate(data.expires_at) : 'Never' }}
						</span>
					</template>
				</Column>
				<Column header-style="width: 40px">
					<template #body="{ data }">
						<DataTableButton
							aria-label="Revoke token"
							icon="fal fa-trash"
							severity="danger"
							title="Revoke token"
							@click="confirmDeleteToken(data.id, data.name)" />
					</template>
				</Column>
			</DataTable>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useApiTokenState } from '@/models/ApiToken/States'
import InputText from 'primevue/inputtext'
import InputNumber from 'primevue/inputnumber'
import Button from 'primevue/button'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Dialog from 'primevue/dialog'
import Message from 'primevue/message'
import FormInput from '@/components/FormInput.vue'
import { format } from 'date-fns'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import DataTableButton from '@/components/Table/DataTableButton.vue'

const emit = defineEmits<{
	(e: 'start-loading'): void
	(e: 'stop-loading'): void
}>()

const toast = useToast()
const confirm = useConfirm()
const tokenState = useApiTokenState()

const newTokenName = ref('')
const newTokenExpiry = ref<number | null>(null)
const creating = ref(false)

const showNewTokenDialog = ref(false)
const newlyCreatedToken = ref('')
const copied = ref(false)

onMounted(async () => {
	await tokenState.fetchTokens()
})

watch(
	tokenState.loading,
	(newVal) => {
		if (newVal) {
			emit('start-loading')
		} else {
			emit('stop-loading')
		}
	},
	{
		immediate: true,
	},
)

function formatDate(dateString: string): string {
	return format(new Date(dateString), 'MMM d, yyyy')
}

function isExpired(expiresAt: string | null): boolean {
	if (!expiresAt) return false
	return new Date(expiresAt) < new Date()
}

async function handleCreateToken() {
	if (!newTokenName.value.trim()) return

	creating.value = true
	copied.value = false

	try {
		const plainTextToken = await tokenState.createToken({
			name: newTokenName.value.trim(),
			expires_in_days: newTokenExpiry.value ?? undefined,
		})

		newlyCreatedToken.value = plainTextToken
		showNewTokenDialog.value = true

		// Reset form
		newTokenName.value = ''
		newTokenExpiry.value = null
	} catch (e: any) {
		toast.add({
			severity: 'error',
			summary: 'Error',
			detail: e.response?.data?.message || 'Failed to create token',
			life: 5000,
		})
	} finally {
		creating.value = false
	}
}

async function handleDeleteToken(id: string) {
	try {
		await tokenState.deleteToken(id)
		toast.add({
			severity: 'success',
			summary: 'Token Revoked',
			detail: 'The API token has been revoked successfully.',
			life: 3000,
		})
	} catch (e: any) {
		toast.add({
			severity: 'error',
			summary: 'Error',
			detail: e.response?.data?.message || 'Failed to delete token',
			life: 5000,
		})
	}
}

function confirmDeleteToken(id: string, name: string) {
	confirm.require({
		message: `Revoke token "${name}"? This action cannot be undone.`,
		header: 'Revoke API Token',
		icon: 'fas fa-exclamation-triangle',
		rejectProps: {
			label: 'Cancel',
			severity: 'secondary',
			outlined: true,
		},
		acceptProps: {
			label: 'Revoke',
			severity: 'danger',
		},
		accept: async () => {
			await handleDeleteToken(id)
		},
		reject: () => {},
	})
}

async function copyToken() {
	try {
		await navigator.clipboard.writeText(newlyCreatedToken.value)
		copied.value = true
	} catch {
		toast.add({
			severity: 'error',
			summary: 'Error',
			detail: 'Failed to copy token to clipboard',
			life: 3000,
		})
	}
}
</script>

<style scoped lang="scss">
.api-tokens {
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: 32px;

	.api-tokens__header {
		display: flex;
		flex-direction: column;
		gap: 8px;

		.api-tokens__title {
			font-size: 24px;
			font-weight: 800;
			margin: 0;
		}

		.api-tokens__description {
			color: var(--p-text-muted-color);
			margin: 0;
			line-height: 1.5;
		}
	}

	.api-tokens__section-title {
		font-size: 18px;
		font-weight: 600;
		margin: 0 0 16px 0;
	}

	.api-tokens__create-section {
		.api-tokens__form {
			display: flex;
			flex-wrap: wrap;
			gap: 16px;
			align-items: flex-end;

			:deep(.form-input) {
				flex: 1;
			}

			.api-tokens__form-submit {
				margin-bottom: 5px;
			}
		}
	}

	.api-tokens__list-section {
		display: flex;
		flex-direction: column;

		.api-tokens__table {
			border-radius: 8px;
			overflow: hidden;

			.api-tokens__empty {
				text-align: center;
				padding: 32px;
				color: var(--p-text-muted-color);
			}
		}

		.api-tokens__expired {
			color: var(--p-red-500);
		}
	}
}
</style>

<style lang="scss">
.api-tokens__dialog {
	max-width: 80vw;

	.api-tokens__new-token-content {
		display: flex;
		flex-direction: column;
		gap: 16px;
	}

	.api-tokens__token-display {
		display: flex;
		align-items: center;
		gap: 8px;
		background: var(--p-surface-800);
		padding: 12px;
		border-radius: 8px;
	}

	.api-tokens__token-value {
		flex: 1;
		word-break: break-all;
		font-family: monospace;
		font-size: 14px;
	}
}
</style>
